import { useState } from "react";
import { Button } from "../shared/Button";
import { Maestro } from "../shared/Icon";
import { cn } from "../../lib/cn";

interface LoginProps {
  onSubmit: (username: string, password: string) => Promise<void>;
}

export function Login({ onSubmit }: LoginProps) {
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  return (
    <div className="h-screen w-screen flex items-center justify-center bg-bg-canvas text-text-primary p-4">
      <div className="w-full max-w-[360px] bg-bg-surface rounded-lg border border-border-subtle shadow-md p-6">
        <div className="flex flex-col items-center mb-6">
          <span
            className="h-10 w-10 rounded-md inline-flex items-center justify-center bg-accent-soft text-accent mb-3"
            aria-hidden="true"
          >
            <Maestro size={20} />
          </span>
          <div className="text-display text-text-primary">Maestro</div>
          <div className="text-small text-text-tertiary mt-1">
            Sign in to continue
          </div>
        </div>
        <form
          className="flex flex-col gap-3"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!u || !p || busy) return;
            setBusy(true);
            setErr(null);
            try {
              await onSubmit(u, p);
            } catch (e) {
              setErr(e instanceof Error ? e.message : "Sign-in failed");
            } finally {
              setBusy(false);
            }
          }}
        >
          <Field label="Username">
            <input
              className={inputClass}
              placeholder="username"
              value={u}
              onChange={(e) => setU(e.target.value)}
              autoFocus
            />
          </Field>
          <Field label="Password">
            <input
              type="password"
              className={inputClass}
              placeholder="••••••••"
              value={p}
              onChange={(e) => setP(e.target.value)}
            />
          </Field>
          {err && (
            <div className="text-small text-status-error mono">{err}</div>
          )}
          <Button
            type="submit"
            variant="primary"
            disabled={busy || !u || !p}
            className="mt-2"
          >
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}

const inputClass = cn(
  "h-10 px-3 bg-bg-inset rounded-md",
  "border border-transparent hover:border-border-default focus:border-border-strong",
  "text-body text-text-primary placeholder:text-text-quaternary outline-none",
  "transition-colors duration-fast ease-out",
);

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-caption text-text-tertiary">{label}</span>
      {children}
    </label>
  );
}
